import styled from "@emotion/styled";
import { Dispatch, SetStateAction, useContext } from "react";

import { Box, IconButton, Typography } from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { OpenInIcon } from "../../../minimals/theme/overrides/CustomIcons";
import { IFrameContext } from "../../contexts/IFrameContext";
import { Contact } from "../types/Contact";

interface IProps {
  contact: Contact;
  index?: number;
  removeContact?: (index: number) => void;
  setEditContactIndex?: Dispatch<SetStateAction<number>>;
  setIsEditMode?: () => void;
  updateContactInPhonebook?: (updateContactObj: Contact) => void;
}

export default function ContactCardNew({
  contact,
  index,
  removeContact,
  setEditContactIndex,
  setIsEditMode,
  updateContactInPhonebook
}: IProps) {
  const { openPhonebookIFrame } = useContext(IFrameContext);
  const isPhonebookContact = !!contact.id;

  const renderEmail = (contact: Contact) => {
    const { email = "", emailsSplit } = contact || {};
    if (!emailsSplit?.length && !email) {
      return <div>--</div>;
    }
    const emailsArr = emailsSplit?.length ? emailsSplit : (email || "").split(";").filter(Boolean); // Filter out empty strings
    return emailsArr.map((email, index) => <div key={`${index}-${email}`}>{email}</div>); // Use index as fallback key
  };

  return (
    <Root>
      <Box
        minHeight={425}
        height={425}
        maxWidth={setIsEditMode ? 230 : undefined}
        width={setIsEditMode ? 230 : undefined}
      >
        <div className="card_top">
          {updateContactInPhonebook && (
            <Box sx={{ position: "absolute", right: 8, top: 8 }}>
              <IconButton
                onClick={() => {
                  openPhonebookIFrame({
                    defaultName: "",
                    onSave: (contact: Contact) => {
                      if (updateContactInPhonebook) {
                        updateContactInPhonebook(contact);
                      }
                    },
                    selectedLookupCode: contact.lookupCode || "",
                    pageView: contact.isIndividual ? "view-contact" : "view-company"
                  });
                }}
                aria-label="open-in"
                sx={{ color: "#fff" }}
              >
                <OpenInIcon />
              </IconButton>
            </Box>
          )}
          {index != null && removeContact && setEditContactIndex && setIsEditMode && (
            <Box sx={{ position: "absolute", right: 8, top: 8 }}>
              <IconButton color="error" size="small" sx={{ color: "#fff" }} onClick={() => removeContact(index)}>
                <Iconify icon={"eva:trash-2-outline"} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => {
                  setEditContactIndex(index), setIsEditMode();
                }}
              >
                <Iconify icon={"eva:edit-fill"} />
              </IconButton>
            </Box>
          )}
         {contact?.name && !contact?.isIndividual ? <CompanyAvatar/> : <Avatar notAllowed={Boolean(!isPhonebookContact && contact?.name)} hasContact={contact?.name} />}
        </div>
        {!contact?.name && <Box textAlign="center">????</Box>}
        <div className="tac">
          <Box textAlign="center">
            {contact?.name && (
              <Typography variant="subtitle1" display="block">
                {contact?.name}
              </Typography>
            )}
            {contact?.contactType?.name && (
              <Typography variant="subtitle2" display="block" style={{ fontStyle: "italic" }}>
                {contact?.contactType?.name === "Other"
                  ? `${contact?.type} - ${contact?.otherContactType}`
                  : contact?.type}
              </Typography>
            )}
            {contact?.role && (
              <Typography variant="subtitle2" fontStyle="italic" display="block">
                {contact?.role}
              </Typography>
            )}
          </Box>
          {(contact?.company && contact?.isIndividual) && (
            <Box textAlign="center">
              <Typography variant="body2">at</Typography> <Typography variant="body2">{contact?.company}</Typography>
            </Box>
          )}
          <div className="contact_info">
            <div>
              <EnvelopeIcon />
              <span>{renderEmail(contact)}</span>
            </div>

            <div>
              <PhoneIcon />
              <span>{contact?.phone ? contact?.phone : "--"}</span>
            </div>
            <div>
              <LocationIcon />
              <Box>
                <Typography variant="body2">{contact?.address1 ? contact?.address1 : "--"}</Typography>
                <Typography variant="body2">
                  {contact?.city} {contact?.state} {contact?.zip}
                </Typography>
              </Box>
            </div>
            <div>
              <SuitcaseIcon />
              <span>{contact?.marketingRep?.fullName ? contact?.marketingRep?.fullName : "--"}</span>
            </div>
          </div>
        </div>
      </Box>
    </Root>
  );
}

export function OtherContactImg({ ...props }) {
  return (
    <OverlayWrap>
      <svg width="266" height="213" viewBox="0 0 266 213" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_dd_410_60)">
          <mask
            id="mask0_410_60"
            style={{ maskType: "luminance" }}
            maskUnits="userSpaceOnUse"
            x="20"
            y="8"
            width="226"
            height="173"
          >
            <path
              d="M229.5 8H36.5C27.6634 8 20.5 15.1634 20.5 24V165C20.5 173.837 27.6634 181 36.5 181H229.5C238.337 181 245.5 173.837 245.5 165V24C245.5 15.1634 238.337 8 229.5 8Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask0_410_60)">
            <path
              d="M229.5 8H36.5C27.6634 8 20.5 15.1634 20.5 24V165C20.5 173.837 27.6634 181 36.5 181H229.5C238.337 181 245.5 173.837 245.5 165V24C245.5 15.1634 238.337 8 229.5 8Z"
              fill="white"
            />
            <mask
              id="mask1_410_60"
              style={{ maskType: "luminance" }}
              maskUnits="userSpaceOnUse"
              x="21"
              y="8"
              width="225"
              height="97"
            >
              <path d="M246 8H21V105H246V8Z" fill="white" />
            </mask>
            <g mask="url(#mask1_410_60)">
              <path d="M246 9H21V106H246V9Z" fill="#EB1C1C" />
              <path d="M246 9H21V106H246V9Z" fill="#141368" />
            </g>
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M172.34 92.88C161.72 82.42 153.84 69 133.6 69H132.4C112.16 69 104.28 82.42 93.66 92.88C85.94 102.52 74.22 104.62 61 105V131H205V105C191.78 104.62 180.06 102.52 172.34 92.88Z"
              fill="white"
            />
            <mask
              id="mask2_410_60"
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="90"
              y="70"
              width="86"
              height="86"
            >
              <path d="M176 70H90V156H176V70Z" fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask2_410_60)">
              <path
                d="M112.242 132.612C115.288 130.462 118.513 128.745 121.917 127.463C125.321 126.177 129.024 125.535 133.025 125.535C137.027 125.535 140.73 126.177 144.134 127.463C147.538 128.745 150.763 130.462 153.809 132.612C156.198 130.163 158.109 127.281 159.542 123.965C160.975 120.652 161.692 116.995 161.692 112.993C161.692 105.05 158.901 98.286 153.318 92.7007C147.733 87.1179 140.968 84.3265 133.025 84.3265C125.082 84.3265 118.319 87.1179 112.737 92.7007C107.151 98.286 104.359 105.05 104.359 112.993C104.359 116.995 105.075 120.652 106.509 123.965C107.942 127.281 109.853 130.163 112.242 132.612ZM133.025 114.785C129.98 114.785 127.427 113.754 125.368 111.692C123.306 109.633 122.275 107.081 122.275 104.035C122.275 100.989 123.306 98.4353 125.368 96.3737C127.427 94.3144 129.98 93.2848 133.025 93.2848C136.071 93.2848 138.624 94.3144 140.683 96.3737C142.745 98.4353 143.775 100.989 143.775 104.035C143.775 107.081 142.745 109.633 140.683 111.692C138.624 113.754 136.071 114.785 133.025 114.785ZM133.025 145.243C128.546 145.243 124.336 144.407 120.394 142.735C116.452 141.063 113.034 138.779 110.139 135.883C107.241 132.986 104.956 129.566 103.284 125.624C101.612 121.683 100.775 117.472 100.775 112.993C100.775 108.514 101.612 104.304 103.284 100.362C104.956 96.4202 107.241 93.0006 110.139 90.1028C113.034 87.2075 116.452 84.9237 120.394 83.2515C124.336 81.5793 128.546 80.7432 133.025 80.7432C137.505 80.7432 141.715 81.5793 145.657 83.2515C149.598 84.9237 153.018 87.2075 155.916 90.1028C158.811 93.0006 161.095 96.4202 162.767 100.362C164.439 104.304 165.275 108.514 165.275 112.993C165.275 117.472 164.439 121.683 162.767 125.624C161.095 129.566 158.811 132.986 155.916 135.883C153.018 138.779 149.598 141.063 145.657 142.735C141.715 144.407 137.505 145.243 133.025 145.243Z"
                fill="#E96651"
              />
              <path
                d="M110.645 130.105C124.733 117.747 139.104 105.653 153 93.0801"
                stroke="#E96651"
                stroke-width="8"
                stroke-linecap="round"
              />
            </g>
            <path
              d="M49.8743 162.432H51.8183V174H50.0663L44.2503 165.512V174H42.3063V162.432H44.2103L49.8743 170.696V162.432ZM57.5829 174.16C56.7935 174.16 56.1055 173.992 55.5189 173.656C54.9322 173.315 54.4762 172.827 54.1509 172.192C53.8309 171.557 53.6709 170.795 53.6709 169.904C53.6709 169.04 53.8255 168.283 54.1349 167.632C54.4495 166.981 54.9002 166.475 55.4869 166.112C56.0735 165.749 56.7749 165.568 57.5909 165.568C58.3802 165.568 59.0655 165.741 59.6469 166.088C60.2282 166.435 60.6762 166.931 60.9909 167.576C61.3109 168.221 61.4709 168.997 61.4709 169.904C61.4709 170.741 61.3189 171.48 61.0149 172.12C60.7162 172.755 60.2762 173.253 59.6949 173.616C59.1189 173.979 58.4149 174.16 57.5829 174.16ZM57.5909 172.68C58.0389 172.68 58.4069 172.557 58.6949 172.312C58.9882 172.067 59.2042 171.731 59.3429 171.304C59.4869 170.877 59.5589 170.392 59.5589 169.848C59.5589 169.341 59.4949 168.875 59.3669 168.448C59.2442 168.021 59.0362 167.68 58.7429 167.424C58.4549 167.163 58.0709 167.032 57.5909 167.032C57.1375 167.032 56.7615 167.152 56.4629 167.392C56.1695 167.627 55.9482 167.957 55.7989 168.384C55.6549 168.805 55.5829 169.293 55.5829 169.848C55.5829 170.349 55.6469 170.816 55.7749 171.248C55.9082 171.675 56.1215 172.021 56.4149 172.288C56.7082 172.549 57.1002 172.68 57.5909 172.68ZM63.1978 174V165.728H65.1018V166.912C65.2298 166.699 65.4031 166.493 65.6218 166.296C65.8458 166.099 66.1204 165.939 66.4458 165.816C66.7711 165.693 67.1524 165.632 67.5898 165.632C68.1018 165.632 68.5764 165.733 69.0138 165.936C69.4564 166.139 69.8111 166.456 70.0778 166.888C70.3498 167.32 70.4858 167.877 70.4858 168.56V174H68.5338V168.792C68.5338 168.248 68.3844 167.845 68.0858 167.584C67.7871 167.317 67.4111 167.184 66.9578 167.184C66.6484 167.184 66.3551 167.237 66.0778 167.344C65.8004 167.445 65.5738 167.603 65.3978 167.816C65.2271 168.024 65.1418 168.285 65.1418 168.6V174H63.1978ZM76.6688 169.144V170.6L72.4448 170.608V169.152L76.6688 169.144ZM79.0406 174V162.432H83.1446C83.9766 162.432 84.7206 162.576 85.3766 162.864C86.038 163.147 86.558 163.565 86.9366 164.12C87.3153 164.675 87.5046 165.357 87.5046 166.168C87.5046 166.931 87.3313 167.584 86.9846 168.128C86.6433 168.672 86.166 169.088 85.5526 169.376C84.9393 169.664 84.222 169.808 83.4006 169.808H81.0166V174H79.0406ZM81.0006 168.248H83.3526C84.03 168.248 84.582 168.045 85.0086 167.64C85.4406 167.235 85.6566 166.707 85.6566 166.056C85.6566 165.395 85.4326 164.883 84.9846 164.52C84.5366 164.152 83.974 163.968 83.2966 163.968H81.0006V168.248ZM89.0571 174V162.112H90.9771V166.928C91.1105 166.72 91.2865 166.52 91.5051 166.328C91.7238 166.131 91.9905 165.971 92.3051 165.848C92.6251 165.72 93.0065 165.656 93.4491 165.656C93.9771 165.656 94.4598 165.757 94.8971 165.96C95.3345 166.163 95.6838 166.456 95.9451 166.84C96.2065 167.219 96.3371 167.672 96.3371 168.2V174H94.3851V168.528C94.3851 168.101 94.2331 167.771 93.9291 167.536C93.6305 167.301 93.2518 167.184 92.7931 167.184C92.4891 167.184 92.1985 167.237 91.9211 167.344C91.6438 167.451 91.4198 167.611 91.2491 167.824C91.0838 168.032 91.0011 168.293 91.0011 168.608V174H89.0571ZM101.849 174.16C101.059 174.16 100.371 173.992 99.7845 173.656C99.1978 173.315 98.7418 172.827 98.4165 172.192C98.0965 171.557 97.9365 170.795 97.9365 169.904C97.9365 169.04 98.0912 168.283 98.4005 167.632C98.7152 166.981 99.1658 166.475 99.7525 166.112C100.339 165.749 101.041 165.568 101.857 165.568C102.646 165.568 103.331 165.741 103.913 166.088C104.494 166.435 104.942 166.931 105.257 167.576C105.577 168.221 105.737 168.997 105.737 169.904C105.737 170.741 105.585 171.48 105.281 172.12C104.982 172.755 104.542 173.253 103.961 173.616C103.385 173.979 102.681 174.16 101.849 174.16ZM101.857 172.68C102.305 172.68 102.673 172.557 102.961 172.312C103.254 172.067 103.47 171.731 103.609 171.304C103.753 170.877 103.825 170.392 103.825 169.848C103.825 169.341 103.761 168.875 103.633 168.448C103.51 168.021 103.302 167.68 103.009 167.424C102.721 167.163 102.337 167.032 101.857 167.032C101.403 167.032 101.027 167.152 100.729 167.392C100.435 167.627 100.214 167.957 100.065 168.384C99.9205 168.805 99.8485 169.293 99.8485 169.848C99.8485 170.349 99.9125 170.816 100.041 171.248C100.174 171.675 100.387 172.021 100.681 172.288C100.974 172.549 101.366 172.68 101.857 172.68ZM107.463 174V165.728H109.367V166.912C109.495 166.699 109.669 166.493 109.887 166.296C110.111 166.099 110.386 165.939 110.711 165.816C111.037 165.693 111.418 165.632 111.855 165.632C112.367 165.632 112.842 165.733 113.279 165.936C113.722 166.139 114.077 166.456 114.343 166.888C114.615 167.32 114.751 167.877 114.751 168.56V174H112.799V168.792C112.799 168.248 112.65 167.845 112.351 167.584C112.053 167.317 111.677 167.184 111.223 167.184C110.914 167.184 110.621 167.237 110.343 167.344C110.066 167.445 109.839 167.603 109.663 167.816C109.493 168.024 109.407 168.285 109.407 168.6V174H107.463ZM118.366 170.312C118.361 170.781 118.436 171.197 118.59 171.56C118.745 171.923 118.974 172.208 119.278 172.416C119.582 172.619 119.958 172.72 120.406 172.72C120.865 172.72 121.262 172.619 121.598 172.416C121.94 172.208 122.166 171.893 122.278 171.472H124.062C123.966 172.048 123.737 172.536 123.374 172.936C123.012 173.336 122.572 173.64 122.054 173.848C121.542 174.056 121.009 174.16 120.454 174.16C119.654 174.16 118.95 173.992 118.342 173.656C117.74 173.315 117.268 172.829 116.926 172.2C116.59 171.571 116.422 170.821 116.422 169.952C116.422 169.104 116.577 168.352 116.886 167.696C117.196 167.035 117.641 166.515 118.222 166.136C118.809 165.757 119.513 165.568 120.334 165.568C121.15 165.568 121.836 165.741 122.39 166.088C122.95 166.435 123.372 166.92 123.654 167.544C123.942 168.163 124.086 168.888 124.086 169.72V170.312H118.366ZM118.366 169.104H122.246C122.246 168.704 122.177 168.344 122.038 168.024C121.9 167.699 121.686 167.443 121.398 167.256C121.116 167.064 120.758 166.968 120.326 166.968C119.9 166.968 119.54 167.072 119.246 167.28C118.953 167.488 118.732 167.757 118.582 168.088C118.433 168.413 118.361 168.752 118.366 169.104ZM130.269 174.16C129.826 174.16 129.448 174.101 129.133 173.984C128.818 173.867 128.554 173.715 128.341 173.528C128.133 173.341 127.965 173.141 127.837 172.928C127.714 172.709 127.621 172.501 127.557 172.304L127.333 174H125.901V162.112H127.853V166.936C127.938 166.776 128.056 166.616 128.205 166.456C128.36 166.296 128.546 166.149 128.765 166.016C128.984 165.883 129.232 165.776 129.509 165.696C129.792 165.611 130.104 165.568 130.445 165.568C131.474 165.568 132.288 165.933 132.885 166.664C133.482 167.395 133.781 168.448 133.781 169.824C133.781 170.693 133.648 171.453 133.381 172.104C133.12 172.755 132.728 173.261 132.205 173.624C131.688 173.981 131.042 174.16 130.269 174.16ZM129.869 172.728C130.45 172.728 130.928 172.496 131.301 172.032C131.674 171.568 131.861 170.816 131.861 169.776C131.861 168.869 131.685 168.184 131.333 167.72C130.981 167.251 130.485 167.016 129.845 167.016C129.386 167.016 129.01 167.12 128.717 167.328C128.429 167.536 128.213 167.845 128.069 168.256C127.93 168.667 127.858 169.173 127.853 169.776C127.853 170.832 128.013 171.589 128.333 172.048C128.658 172.501 129.17 172.728 129.869 172.728ZM138.942 174.16C138.153 174.16 137.465 173.992 136.878 173.656C136.292 173.315 135.836 172.827 135.51 172.192C135.19 171.557 135.03 170.795 135.03 169.904C135.03 169.04 135.185 168.283 135.494 167.632C135.809 166.981 136.26 166.475 136.846 166.112C137.433 165.749 138.134 165.568 138.95 165.568C139.74 165.568 140.425 165.741 141.006 166.088C141.588 166.435 142.036 166.931 142.35 167.576C142.67 168.221 142.83 168.997 142.83 169.904C142.83 170.741 142.678 171.48 142.374 172.12C142.076 172.755 141.636 173.253 141.054 173.616C140.478 173.979 139.774 174.16 138.942 174.16ZM138.95 172.68C139.398 172.68 139.766 172.557 140.054 172.312C140.348 172.067 140.564 171.731 140.702 171.304C140.846 170.877 140.918 170.392 140.918 169.848C140.918 169.341 140.854 168.875 140.726 168.448C140.604 168.021 140.396 167.68 140.102 167.424C139.814 167.163 139.43 167.032 138.95 167.032C138.497 167.032 138.121 167.152 137.822 167.392C137.529 167.627 137.308 167.957 137.158 168.384C137.014 168.805 136.942 169.293 136.942 169.848C136.942 170.349 137.006 170.816 137.134 171.248C137.268 171.675 137.481 172.021 137.774 172.288C138.068 172.549 138.46 172.68 138.95 172.68ZM147.989 174.16C147.2 174.16 146.512 173.992 145.925 173.656C145.338 173.315 144.882 172.827 144.557 172.192C144.237 171.557 144.077 170.795 144.077 169.904C144.077 169.04 144.232 168.283 144.541 167.632C144.856 166.981 145.306 166.475 145.893 166.112C146.48 165.749 147.181 165.568 147.997 165.568C148.786 165.568 149.472 165.741 150.053 166.088C150.634 166.435 151.082 166.931 151.397 167.576C151.717 168.221 151.877 168.997 151.877 169.904C151.877 170.741 151.725 171.48 151.421 172.12C151.122 172.755 150.682 173.253 150.101 173.616C149.525 173.979 148.821 174.16 147.989 174.16ZM147.997 172.68C148.445 172.68 148.813 172.557 149.101 172.312C149.394 172.067 149.61 171.731 149.749 171.304C149.893 170.877 149.965 170.392 149.965 169.848C149.965 169.341 149.901 168.875 149.773 168.448C149.65 168.021 149.442 167.68 149.149 167.424C148.861 167.163 148.477 167.032 147.997 167.032C147.544 167.032 147.168 167.152 146.869 167.392C146.576 167.627 146.354 167.957 146.205 168.384C146.061 168.805 145.989 169.293 145.989 169.848C145.989 170.349 146.053 170.816 146.181 171.248C146.314 171.675 146.528 172.021 146.821 172.288C147.114 172.549 147.506 172.68 147.997 172.68ZM161.26 174H159.284L156.884 170.168L155.54 171.576V174H153.604V162.112H155.54V169.48L158.972 165.728H161.108L158.116 168.96L161.26 174ZM171.107 162.272C171.955 162.272 172.707 162.445 173.363 162.792C174.019 163.133 174.547 163.597 174.947 164.184C175.352 164.771 175.592 165.424 175.667 166.144H173.611C173.531 165.717 173.382 165.336 173.163 165C172.95 164.664 172.667 164.397 172.315 164.2C171.963 164.003 171.542 163.904 171.051 163.904C170.438 163.904 169.915 164.051 169.483 164.344C169.056 164.632 168.728 165.091 168.499 165.72C168.275 166.349 168.163 167.165 168.163 168.168C168.163 169.688 168.414 170.795 168.915 171.488C169.416 172.181 170.128 172.528 171.051 172.528C171.542 172.528 171.963 172.421 172.315 172.208C172.667 171.989 172.95 171.701 173.163 171.344C173.382 170.981 173.531 170.587 173.611 170.16H175.667C175.603 170.683 175.47 171.184 175.267 171.664C175.07 172.144 174.79 172.573 174.427 172.952C174.064 173.325 173.608 173.621 173.059 173.84C172.515 174.053 171.864 174.16 171.107 174.16C170.046 174.16 169.134 173.917 168.371 173.432C167.614 172.941 167.03 172.248 166.619 171.352C166.214 170.451 166.011 169.381 166.011 168.144C166.011 166.912 166.214 165.859 166.619 164.984C167.024 164.109 167.606 163.44 168.363 162.976C169.126 162.507 170.04 162.272 171.107 162.272ZM181.005 174.16C180.215 174.16 179.527 173.992 178.941 173.656C178.354 173.315 177.898 172.827 177.573 172.192C177.253 171.557 177.093 170.795 177.093 169.904C177.093 169.04 177.247 168.283 177.557 167.632C177.871 166.981 178.322 166.475 178.909 166.112C179.495 165.749 180.197 165.568 181.013 165.568C181.802 165.568 182.487 165.741 183.069 166.088C183.65 166.435 184.098 166.931 184.413 167.576C184.733 168.221 184.893 168.997 184.893 169.904C184.893 170.741 184.741 171.48 184.437 172.12C184.138 172.755 183.698 173.253 183.117 173.616C182.541 173.979 181.837 174.16 181.005 174.16ZM181.013 172.68C181.461 172.68 181.829 172.557 182.117 172.312C182.41 172.067 182.626 171.731 182.765 171.304C182.909 170.877 182.981 170.392 182.981 169.848C182.981 169.341 182.917 168.875 182.789 168.448C182.666 168.021 182.458 167.68 182.165 167.424C181.877 167.163 181.493 167.032 181.013 167.032C180.559 167.032 180.183 167.152 179.885 167.392C179.591 167.627 179.37 167.957 179.221 168.384C179.077 168.805 179.005 169.293 179.005 169.848C179.005 170.349 179.069 170.816 179.197 171.248C179.33 171.675 179.543 172.021 179.837 172.288C180.13 172.549 180.522 172.68 181.013 172.68ZM186.62 174V165.728H188.524V166.912C188.652 166.699 188.825 166.493 189.044 166.296C189.268 166.099 189.542 165.939 189.868 165.816C190.193 165.693 190.574 165.632 191.012 165.632C191.524 165.632 191.998 165.733 192.436 165.936C192.878 166.139 193.233 166.456 193.5 166.888C193.772 167.32 193.908 167.877 193.908 168.56V174H191.956V168.792C191.956 168.248 191.806 167.845 191.508 167.584C191.209 167.317 190.833 167.184 190.38 167.184C190.07 167.184 189.777 167.237 189.5 167.344C189.222 167.445 188.996 167.603 188.82 167.816C188.649 168.024 188.564 168.285 188.564 168.6V174H186.62ZM200.277 167.104H198.429L198.437 171.848C198.437 172.093 198.463 172.275 198.517 172.392C198.575 172.504 198.666 172.579 198.789 172.616C198.917 172.648 199.087 172.664 199.301 172.664H200.317V173.896C200.21 173.939 200.047 173.976 199.829 174.008C199.615 174.045 199.327 174.064 198.965 174.064C198.309 174.064 197.799 173.979 197.437 173.808C197.079 173.632 196.831 173.384 196.693 173.064C196.554 172.744 196.485 172.363 196.485 171.92V167.104H195.141V165.728H196.541L197.029 163.272H198.429V165.72H200.277V167.104ZM204.495 174.16C203.967 174.16 203.492 174.064 203.071 173.872C202.655 173.675 202.327 173.395 202.087 173.032C201.852 172.664 201.735 172.219 201.735 171.696C201.735 170.784 202.063 170.096 202.719 169.632C203.38 169.168 204.434 168.925 205.879 168.904L207.111 168.872V168.312C207.111 167.875 206.98 167.533 206.719 167.288C206.463 167.037 206.063 166.915 205.519 166.92C205.119 166.925 204.751 167.021 204.415 167.208C204.084 167.389 203.866 167.696 203.759 168.128H202.103C202.135 167.563 202.298 167.091 202.591 166.712C202.89 166.328 203.298 166.043 203.815 165.856C204.332 165.664 204.93 165.568 205.607 165.568C206.396 165.568 207.039 165.672 207.535 165.88C208.031 166.088 208.396 166.389 208.631 166.784C208.866 167.173 208.983 167.645 208.983 168.2V174H207.343L207.191 172.512C206.892 173.136 206.514 173.568 206.055 173.808C205.602 174.043 205.082 174.16 204.495 174.16ZM205.127 172.84C205.367 172.84 205.604 172.797 205.839 172.712C206.074 172.621 206.287 172.501 206.479 172.352C206.671 172.197 206.823 172.027 206.935 171.84C207.052 171.653 207.111 171.459 207.111 171.256V169.952L206.095 169.968C205.594 169.973 205.156 170.027 204.783 170.128C204.41 170.229 204.116 170.392 203.903 170.616C203.695 170.835 203.591 171.128 203.591 171.496C203.591 171.917 203.738 172.248 204.031 172.488C204.324 172.723 204.69 172.84 205.127 172.84ZM214.692 165.568C215.338 165.568 215.906 165.696 216.396 165.952C216.887 166.208 217.279 166.563 217.572 167.016C217.871 167.469 218.05 167.984 218.108 168.56H216.444C216.407 168.309 216.316 168.072 216.172 167.848C216.028 167.619 215.834 167.435 215.588 167.296C215.343 167.152 215.047 167.08 214.7 167.08C214.082 167.08 213.578 167.309 213.188 167.768C212.804 168.227 212.612 168.931 212.612 169.88C212.612 170.755 212.796 171.451 213.164 171.968C213.532 172.485 214.058 172.744 214.74 172.744C215.082 172.744 215.372 172.669 215.612 172.52C215.858 172.365 216.05 172.176 216.188 171.952C216.332 171.723 216.426 171.493 216.468 171.264H218.084C218.036 171.824 217.86 172.323 217.556 172.76C217.258 173.197 216.863 173.541 216.372 173.792C215.887 174.037 215.327 174.16 214.692 174.16C213.935 174.16 213.26 173.995 212.668 173.664C212.082 173.328 211.62 172.843 211.284 172.208C210.948 171.568 210.78 170.797 210.78 169.896C210.78 169.037 210.938 168.285 211.252 167.64C211.567 166.989 212.018 166.483 212.604 166.12C213.191 165.752 213.887 165.568 214.692 165.568ZM224.292 167.104H222.444L222.452 171.848C222.452 172.093 222.479 172.275 222.532 172.392C222.591 172.504 222.682 172.579 222.804 172.616C222.932 172.648 223.103 172.664 223.316 172.664H224.332V173.896C224.226 173.939 224.063 173.976 223.844 174.008C223.631 174.045 223.343 174.064 222.98 174.064C222.324 174.064 221.815 173.979 221.452 173.808C221.095 173.632 220.847 173.384 220.708 173.064C220.57 172.744 220.5 172.363 220.5 171.92V167.104H219.156V165.728H220.556L221.044 163.272H222.444V165.72H224.292V167.104Z"
              fill="black"
            />
          </g>
        </g>
        <defs>
          <filter
            id="filter0_dd_410_60"
            x="-3.5"
            y="-4"
            width="273"
            height="221"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="12" />
            <feGaussianBlur stdDeviation="12" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.568627 0 0 0 0 0.619608 0 0 0 0 0.670588 0 0 0 0.12 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_410_60" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset />
            <feGaussianBlur stdDeviation="1" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.568627 0 0 0 0 0.619608 0 0 0 0 0.670588 0 0 0 0.2 0" />
            <feBlend mode="normal" in2="effect1_dropShadow_410_60" result="effect2_dropShadow_410_60" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_410_60" result="shape" />
          </filter>
        </defs>
      </svg>
    </OverlayWrap>
  );
}

// --------------- STYLES ---------------

export const Root = styled.div`
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0px 0px 2px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12);
  max-width: 240px;
  overflow: auto;
  padding-bottom: 24px;
  width: 100%;
  .card_top {
    margin-bottom: 4px;
    position: relative;
    svg {
      height: auto;
      max-width: 100%;
      width: 100%;
    }
  }
  .contact_info {
    padding: 0 20px;
    text-align: left;
    > div {
      align-items: center;
      display: flex;
      font-size: 12px;
      gap: 8px;
      margin-top: 16px;
      :first-of-type {
        margin-top: 24px;
      }
      svg {
        flex: 0 0 20px;
      }
    }
  }
`;

const OverlayWrap = styled.div`
  position: relative;
  /* display: flex;
  align-items: flex-start; */
  overflow: hidden;
  border-radius: 16px;
  height: 172px;
  svg {
    width: 100%;
    height: auto;
    position: relative;
    top: -8px;
  }
  :before {
    background-color: rgba(0, 0, 0, 0.155);
    border-radius: 16px;
    bottom: 0;
    content: "";
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;
    width: 100%;
    width: calc(100% - 42px);
    height: auto;
    left: 21px;
  }
`;

export function Avatar({ notAllowed, hasContact, ...props }: { notAllowed: any; hasContact: any }) {
  return (
    <>
      {notAllowed ? (
        <svg width="226" height="148" viewBox="0 0 226 148" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_408_45)">
            <mask
              id="mask0_408_45"
              style={{ maskType: "luminance" }}
              maskUnits="userSpaceOnUse"
              x="-59"
              y="0"
              width="344"
              height="97"
            >
              <path d="M285 0H-59V97H285V0Z" fill="white" />
            </mask>
            <g mask="url(#mask0_408_45)">
              <path d="M285 0H-59V97H285V0Z" fill="#EB1C1C" />
              <path d="M285 0H-59V97H285V0Z" fill="#141368" />
            </g>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M152.34 84.88C141.72 74.42 133.84 61 113.6 61H112.4C92.16 61 84.28 74.42 73.66 84.88C65.94 94.52 54.22 96.62 41 97V123H185V97C171.78 96.62 160.06 94.52 152.34 84.88Z"
              fill="white"
            />
            <mask
              id="mask1_408_45"
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="70"
              y="62"
              width="86"
              height="86"
            >
              <path d="M156 62H70V148H156V62Z" fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask1_408_45)">
              <path
                d="M92.2421 124.612C95.2879 122.462 98.5129 120.745 101.917 119.463C105.321 118.177 109.024 117.535 113.025 117.535C117.027 117.535 120.73 118.177 124.134 119.463C127.538 120.745 130.763 122.462 133.809 124.612C136.198 122.163 138.109 119.281 139.542 115.965C140.975 112.652 141.692 108.995 141.692 104.993C141.692 97.0501 138.901 90.286 133.318 84.7007C127.733 79.1179 120.968 76.3265 113.025 76.3265C105.082 76.3265 98.3194 79.1179 92.7366 84.7007C87.1513 90.286 84.3587 97.0501 84.3587 104.993C84.3587 108.995 85.0754 112.652 86.5087 115.965C87.9421 119.281 89.8532 122.163 92.2421 124.612ZM113.025 106.785C109.98 106.785 107.427 105.754 105.368 103.692C103.306 101.633 102.275 99.0807 102.275 96.0348C102.275 92.989 103.306 90.4353 105.368 88.3737C107.427 86.3144 109.98 85.2848 113.025 85.2848C116.071 85.2848 118.624 86.3144 120.683 88.3737C122.745 90.4353 123.775 92.989 123.775 96.0348C123.775 99.0807 122.745 101.633 120.683 103.692C118.624 105.754 116.071 106.785 113.025 106.785ZM113.025 137.243C108.546 137.243 104.336 136.407 100.394 134.735C96.4525 133.063 93.034 130.779 90.1386 127.883C87.2409 124.986 84.9559 121.566 83.2837 117.624C81.6115 113.683 80.7754 109.472 80.7754 104.993C80.7754 100.514 81.6115 96.3036 83.2837 92.3619C84.9559 88.4202 87.2409 85.0006 90.1386 82.1028C93.034 79.2075 96.4525 76.9237 100.394 75.2515C104.336 73.5793 108.546 72.7432 113.025 72.7432C117.505 72.7432 121.715 73.5793 125.657 75.2515C129.598 76.9237 133.018 79.2075 135.916 82.1028C138.811 85.0006 141.095 88.4202 142.767 92.3619C144.439 96.3036 145.275 100.514 145.275 104.993C145.275 109.472 144.439 113.683 142.767 117.624C141.095 121.566 138.811 124.986 135.916 127.883C133.018 130.779 129.598 133.063 125.657 134.735C121.715 136.407 117.505 137.243 113.025 137.243Z"
                fill="#E96651"
              />
              <path
                d="M90.6445 122.105C104.733 109.747 119.104 97.6531 133 85.0801"
                stroke="#E96651"
                strokeWidth="8"
                strokeLinecap="round"
              />
            </g>
          </g>
          <defs>
            <clipPath id="clip0_408_45">
              <rect width="226" height="148" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ) : (
        <svg width="225" height="148" viewBox="0 0 225 148" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
          <g clipPath="url(#clip0_401_4)">
            <mask
              id="mask0_401_4"
              style={{ maskType: "luminance" }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="225"
              height="97"
            >
              <path d="M225 0H0V97H225V0Z" fill="white" />
            </mask>
            <g mask="url(#mask0_401_4)">
              <path d="M225 0H0V97H225V0Z" fill={hasContact ? "#141368" : "rgba(20, 19, 104, 0.5)"} />
            </g>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M151.84 84.88C141.22 74.42 133.34 61 113.1 61H111.9C91.66 61 83.78 74.42 73.16 84.88C65.44 94.52 53.72 96.62 40.5 97V123H184.5V97C171.28 96.62 159.56 94.52 151.84 84.88Z"
              fill="white"
            />
            <mask
              id="mask1_401_4"
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="69"
              y="62"
              width="87"
              height="86"
            >
              <path d="M155.5 62H69.5V148H155.5V62Z" fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask1_401_4)">
              <path
                d="M91.7186 124.619C94.7645 122.469 97.9895 120.752 101.394 119.469C104.798 118.184 108.501 117.542 112.502 117.542C116.503 117.542 120.206 118.184 123.61 119.469C127.014 120.752 130.239 122.469 133.285 124.619C135.674 122.17 137.585 119.288 139.019 115.972C140.452 112.659 141.169 109.001 141.169 105C141.169 97.0569 138.377 90.2928 132.794 84.7076C127.209 79.1247 120.445 76.3333 112.502 76.3333C104.559 76.3333 97.796 79.1247 92.2131 84.7076C86.6279 90.2928 83.8353 97.0569 83.8353 105C83.8353 109.001 84.552 112.659 85.9853 115.972C87.4186 119.288 89.3297 122.17 91.7186 124.619ZM112.502 106.792C109.456 106.792 106.904 105.761 104.844 103.699C102.783 101.64 101.752 99.0875 101.752 96.0417C101.752 92.9958 102.783 90.4421 104.844 88.3805C106.904 86.3213 109.456 85.2917 112.502 85.2917C115.548 85.2917 118.1 86.3213 120.16 88.3805C122.221 90.4421 123.252 92.9958 123.252 96.0417C123.252 99.0875 122.221 101.64 120.16 103.699C118.1 105.761 115.548 106.792 112.502 106.792ZM112.502 137.25C108.023 137.25 103.812 136.414 99.8707 134.742C95.929 133.069 92.5105 130.786 89.6152 127.89C86.7175 124.993 84.4325 121.573 82.7603 117.631C81.0881 113.69 80.252 109.479 80.252 105C80.252 100.521 81.0881 96.3104 82.7603 92.3688C84.4325 88.4271 86.7175 85.0074 89.6152 82.1097C92.5105 79.2143 95.929 76.9306 99.8707 75.2583C103.812 73.5861 108.023 72.75 112.502 72.75C116.981 72.75 121.192 73.5861 125.133 75.2583C129.075 76.9306 132.495 79.2143 135.392 82.1097C138.288 85.0074 140.571 88.4271 142.244 92.3688C143.916 96.3104 144.752 100.521 144.752 105C144.752 109.479 143.916 113.69 142.244 117.631C140.571 121.573 138.288 124.993 135.392 127.89C132.495 130.786 129.075 133.069 125.133 134.742C121.192 136.414 116.981 137.25 112.502 137.25Z"
                fill={hasContact ? "#141368" : "rgba(20, 19, 104, 0.5)"}
              />
            </g>
          </g>
          <defs>
            <clipPath id="clip0_401_4">
              <rect width="225" height="148" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )}
    </>
  );
}

export function EnvelopeIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.66797 3.8335H16.3346C17.7153 3.8335 18.8346 4.95278 18.8346 6.3335V14.6668C18.8346 16.0475 17.7153 17.1668 16.3346 17.1668H4.66797C3.28726 17.1668 2.16797 16.0475 2.16797 14.6668V6.3335C2.16797 4.95278 3.28726 3.8335 4.66797 3.8335ZM10.918 9.22516L16.3346 5.50016H4.66797L10.0846 9.22516C10.3425 9.37402 10.6601 9.37402 10.918 9.22516Z"
        fill="black"
      />
    </svg>
  );
}
export function PhoneIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.55 17.0001C7.17454 16.9918 2.00826 11.8255 2 5.45009C2 3.5447 3.54462 2.00009 5.45 2.00009C5.64376 1.99861 5.83719 2.01619 6.0275 2.05259C6.21148 2.07981 6.39235 2.12503 6.5675 2.18759C6.81814 2.27553 7.00357 2.48949 7.055 2.75009L8.0825 7.25009C8.13877 7.49827 8.06535 7.75806 7.8875 7.94009C7.79 8.04509 7.7825 8.05259 6.86 8.53259C7.59875 10.1532 8.8949 11.4547 10.5125 12.2001C11 11.2701 11.0075 11.2626 11.1125 11.1651C11.2945 10.9872 11.5543 10.9138 11.8025 10.9701L16.3025 11.9976C16.5545 12.0561 16.7586 12.2403 16.8425 12.4851C16.9058 12.6631 16.9535 12.8463 16.985 13.0326C17.0152 13.2211 17.0302 13.4117 17.03 13.6026C17.0012 15.4992 15.4467 17.0168 13.55 17.0001V17.0001Z"
        fill="#212B36"
      />
    </svg>
  );
}
export function LocationIcon() {
  return (
    <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="10.5" cy="7.9165" rx="1.25" ry="1.25" fill="#212B36" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.83203 8.2665C3.86859 4.61067 6.84268 1.66632 10.4987 1.6665C14.1547 1.66632 17.1288 4.61067 17.1654 8.2665C17.1654 12.8332 11.332 17.9165 11.0404 18.1332C10.7285 18.3999 10.2689 18.3999 9.95703 18.1332L9.95567 18.132C9.68657 17.8988 3.83203 12.8252 3.83203 8.2665ZM7.58203 7.9165C7.58203 9.52733 8.88787 10.8332 10.4987 10.8332C11.2722 10.8332 12.0141 10.5259 12.5611 9.9789C13.1081 9.43192 13.4154 8.69005 13.4154 7.9165C13.4154 6.30567 12.1095 4.99984 10.4987 4.99984C8.88787 4.99984 7.58203 6.30567 7.58203 7.9165Z"
        fill="#212B36"
      />
    </svg>
  );
}
export function SuitcaseIcon() {
  return (
    <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.6654 17.5H6.33203V5.83333H7.16536V4.58333C7.16536 3.43274 8.0981 2.5 9.2487 2.5H11.7487C12.8993 2.5 13.832 3.43274 13.832 4.58333V5.83333H14.6654V17.5ZM9.2487 4.16667C9.01858 4.16667 8.83203 4.35321 8.83203 4.58333V5.83333H12.1654V4.58333C12.1654 4.35321 11.9788 4.16667 11.7487 4.16667H9.2487Z"
        fill="black"
      />
      <path
        d="M16.332 5.8335V17.5002C17.7127 17.5002 18.832 16.3809 18.832 15.0002V8.3335C18.832 6.95278 17.7127 5.8335 16.332 5.8335Z"
        fill="black"
      />
      <path
        d="M4.66797 5.8335C3.28726 5.8335 2.16797 6.95278 2.16797 8.3335V15.0002C2.16797 16.3809 3.28726 17.5002 4.66797 17.5002V5.8335Z"
        fill="black"
      />
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
      <path d="m0 0h24v24h-24z" fill="none" />
      <path d="m15.5 14h-.79l-.28-.27c.98-1.14 1.57-2.62 1.57-4.23 0-3.59-2.91-6.5-6.5-6.5s-6.5 2.91-6.5 6.5 2.91 6.5 6.5 6.5c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99 1.49-1.49zm-6 0c-2.49 0-4.5-2.01-4.5-4.5s2.01-4.5 4.5-4.5 4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5z" />
    </svg>
  );
}
export function CompanyAvatar() {
  return (
    <svg
      width='344'
      height='139'
      viewBox='0 0 344 139'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <g clip-path='url(#clip0_11811_59428)'>
        <rect width='344' height='97' fill='#EB1C1C' />
        <rect width='344' height='97' fill='#141368' />
      </g>
      <path
        fill-rule='evenodd'
        clip-rule='evenodd'
        d='M211.34 93.88C200.72 83.42 192.84 70 172.6 70H171.4C151.16 70 143.28 83.42 132.66 93.88C124.94 103.52 113.22 105.62 100 106V132H244V106C230.78 105.62 219.06 103.52 211.34 93.88Z'
        fill='white'
      />
      <path
        d='M187.083 132.75V99.4167C187.083 97.1155 185.218 95.25 182.917 95.25H162.083C159.782 95.25 157.917 97.1155 157.917 99.4167V132.75M187.083 132.75L191.25 132.75M187.083 132.75H176.667M157.917 132.75L153.75 132.75M157.917 132.75H168.333M166.25 103.583H168.333M166.25 111.917H168.333M176.667 103.583H178.75M176.667 111.917H178.75M168.333 132.75V122.333C168.333 121.183 169.266 120.25 170.417 120.25H174.583C175.734 120.25 176.667 121.183 176.667 122.333V132.75M168.333 132.75H176.667'
        stroke='#141368'
        stroke-width='5'
        stroke-linecap='round'
        stroke-linejoin='round'
      />
      <defs>
        <clipPath id='clip0_11811_59428'>
          <rect width='344' height='97' fill='white' />
        </clipPath>
      </defs>
    </svg>
  );
}